import { z } from "zod";
import { ActorId, CountryCode } from "./primitives";

/**
 * Named world copies (ADR-0043 decisions 4-8). Several copies of the ONE town can run at once; each is a street room
 * (`street:{worldVersion}:town:{copy}`) with its own crowd, street chat, AI residents and traffic. Everything stored in
 * PostgreSQL is shared by every copy (ownership, prices, signs, billboards, offers, coins and credit, homes,
 * statistics), and interiors are NOT copied: each property keeps ONE shared interior room, where people from every copy
 * meet. Robin (copy 1) is always open; the others open when the open ones are full and close when they have been empty
 * for a while (`streetCopyEmptyGraceSeconds`).
 */

/**
 * The copies' names, in order: copy n is `WORLD_COPY_NAMES[n - 1]`. A FIXED list (founder decision 2026-10-24): a name
 * always means the same copy number, so append new names at the end and never reorder, rename or reuse one.
 */
export const WORLD_COPY_NAMES = [
  "Robin",
  "Finch",
  "Wren",
  "Heron",
  "Lark",
  "Swift",
  "Plover",
  "Kestrel",
  "Oriole",
  "Puffin",
  "Sparrow",
  "Tern",
  "Egret",
  "Dove",
  "Magpie",
  "Starling",
] as const;
export type WorldCopyName = (typeof WORLD_COPY_NAMES)[number];

/** Robin: the first copy, always open (the town never has fewer than one copy). */
export const ROBIN_COPY = 1;
/** At most this many copies at once (one per name). */
export const MAX_WORLD_COPIES = WORLD_COPY_NAMES.length;

export const WorldCopyNumber = z.number().int().min(1).max(MAX_WORLD_COPIES);
export type WorldCopyNumber = z.infer<typeof WorldCopyNumber>;

/** "Robin" for 1, "Finch" for 2, ...; a number outside the list (never issued) reads "World n". */
export function worldCopyName(copy: number): string {
  return WORLD_COPY_NAMES[copy - 1] ?? `World ${copy}`;
}

export function isWorldCopyNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 1 && value <= MAX_WORLD_COPIES;
}

/**
 * A copy number from a join option (the decimal string a ticket's join options carry, or a plain integer), or null when
 * it is missing, malformed or outside 1..MAX_WORLD_COPIES. Join options select a room only; the ticket's room key, which
 * names the copy, is what authorises the join.
 */
export function parseCopyOption(raw: unknown): number | null {
  const n = typeof raw === "number" ? raw : typeof raw === "string" && /^\d{1,2}$/.test(raw) ? Number(raw) : Number.NaN;
  return isWorldCopyNumber(n) ? n : null;
}

/** The copy of a town street room key (`street:6:town:3` -> 3); null for an interior, an older world's key or nonsense. */
export function streetCopyOf(roomKey: string | null | undefined, worldVersion?: number): number | null {
  if (!roomKey) return null;
  const m = /^street:(\d{1,6}):town:(\d{1,2})$/.exec(roomKey);
  if (!m) return null;
  if (worldVersion !== undefined && Number(m[1]) !== worldVersion) return null;
  return parseCopyOption(m[2]);
}

/**
 * The copy a presence entry (the realtime heartbeat, server-observed) puts an actor in: its street room's copy, or inside
 * a building the copy it entered from (`copy`); null when neither is known (an older world's key, no `fromCopy`).
 */
export function copyOfPresenceEntry(p: { roomKey: string; copy?: number | null } | null | undefined, worldVersion: number): number | null {
  if (!p) return null;
  const fromKey = streetCopyOf(p.roomKey, worldVersion);
  if (fromKey !== null) return fromKey;
  return p.roomKey.startsWith("interior:") && isWorldCopyNumber(p.copy) ? p.copy : null;
}

/** What the placement rule knows about one open copy (from the realtime rooms' directory in Redis, never a client). */
export interface StreetCopyOccupancy {
  copy: number;
  /** Visitors in the copy (the room state's `peopleCount`: AI residents never count). */
  people: number;
  /** Seats taken: connected tabs plus reserved seats (arrivals being admitted, visitors inside their reconnection grace). */
  seats: number;
  /** The copy's cap (`roomCaps.street`, or the local/test override STREET_ROOM_CAP of its realtime process). */
  cap: number;
}

export interface ChooseStreetCopyOptions {
  /** The cap of a copy that is not running yet (Robin before its first visitor, the next copy to open). */
  defaultCap: number;
  /** How many copies may run (MAX_WORLD_COPIES; 1 when copies are switched off). */
  maxCopies: number;
  /** A copy the visitor should stay in when it is open and has room (reload, leaving a building). */
  prefer?: number | null;
  /** A copy that just refused this visitor with ROOM_FULL (the one retry after a race): never chosen this time. */
  avoid?: number | null;
}

/**
 * The placement rule (ADR-0043 decision 5): the copy a visitor is put in.
 *  1. `prefer`, when it is open (Robin always is) and below its cap;
 *  2. else the FULLEST open copy below its cap (crowds stay lively; ties go to the lower number);
 *  3. else (every open copy is full) the lowest copy number that is not open: a new copy opens;
 *  4. else null: every copy is full (the visitor gets ROOM_FULL with a retry).
 * Robin (copy 1) is open even while its room is not running (no entry: no seats taken). `avoid` is left out of every
 * step: it can open at most one copy that would not otherwise be needed (every other open copy must be full).
 */
export function chooseStreetCopy(open: readonly StreetCopyOccupancy[], opts: ChooseStreetCopyOptions): number | null {
  const maxCopies = Math.max(1, Math.min(MAX_WORLD_COPIES, Math.floor(opts.maxCopies)));
  const byCopy = new Map<number, StreetCopyOccupancy>();
  for (const c of open) if (isWorldCopyNumber(c.copy) && c.copy <= maxCopies) byCopy.set(c.copy, c);
  if (!byCopy.has(ROBIN_COPY)) byCopy.set(ROBIN_COPY, { copy: ROBIN_COPY, people: 0, seats: 0, cap: opts.defaultCap });
  const avoid = opts.avoid ?? null;
  const hasRoom = (c: StreetCopyOccupancy) => c.copy !== avoid && c.seats < c.cap;
  const preferred = opts.prefer ? byCopy.get(opts.prefer) : undefined;
  if (preferred && hasRoom(preferred)) return preferred.copy;
  let best: StreetCopyOccupancy | null = null;
  for (const c of byCopy.values()) {
    if (!hasRoom(c)) continue;
    if (!best || c.seats > best.seats || (c.seats === best.seats && c.copy < best.copy)) best = c;
  }
  if (best) return best.copy;
  for (let copy = 1; copy <= maxCopies; copy++) if (!byCopy.has(copy) && copy !== avoid) return copy;
  return null;
}

/**
 * Who may see which world an account is in (ADR-0043 decision 7), an account setting:
 *  - `messaged` (default): people this account has sent a private message to (a kept message from this account in
 *    their conversation; messages are kept DM_RETENTION_DAYS unless a report holds them);
 *  - `everyone`: anyone in the town, guests too;
 *  - `nobody`.
 * Enforced by the server on every lookup and every join. A mute either way always hides. Guests have no setting and are
 * visible only to people they have messaged: guests cannot send private messages, so nobody sees a guest's world.
 */
export const WorldVisibility = z.enum(["messaged", "everyone", "nobody"]);
export type WorldVisibility = z.infer<typeof WorldVisibility>;
export const DEFAULT_WORLD_VISIBILITY: WorldVisibility = "messaged";

/** GET / PUT /api/v1/me/world-settings. */
export const WorldSettings = z.object({ visibility: WorldVisibility }).strict();
export type WorldSettings = z.infer<typeof WorldSettings>;

/** One world copy as the Worlds list shows it. */
export const WorldCopyView = z.object({
  copy: WorldCopyNumber,
  name: z.string(),
  /** Visitors in it now (AI residents never count). */
  people: z.number().int().min(0),
  /** No place left: switching there is refused. */
  full: z.boolean(),
});
export type WorldCopyView = z.infer<typeof WorldCopyView>;

/** GET /api/v1/worlds: every open copy (Robin always), lowest number first. */
export const WorldsResponse = z.object({ copies: z.array(WorldCopyView), cap: z.number().int().min(1) });
export type WorldsResponse = z.infer<typeof WorldsResponse>;

/** The world someone is in, as a lookup the server allowed shows it. */
export const PersonWorld = z.object({ copy: WorldCopyNumber, name: z.string() });
export type PersonWorld = z.infer<typeof PersonWorld>;

/**
 * GET /api/v1/people/:actorId/world. `world: null` means "not shown": the person is offline, unknown, a guest, hidden by
 * their setting, or muted either way. All of these are the same answer (status 200), so the response never tells a
 * hidden person from one who does not exist.
 */
export const PersonWorldResponse = z.object({ world: PersonWorld.nullable() });
export type PersonWorldResponse = z.infer<typeof PersonWorldResponse>;

/** "Find a person": a display name or its beginning (3-20 letters, digits or underscores; case does not matter). */
export const PERSON_SEARCH_MIN_CHARS = 3;
export const PERSON_SEARCH_MAX_RESULTS = 10;
export const PersonSearchQuery = z
  .object({
    q: z
      .string()
      .trim()
      .regex(/^[A-Za-z0-9_]{3,20}$/, "Type at least 3 letters or digits of a display name."),
  })
  .strict();
export type PersonSearchQuery = z.infer<typeof PersonSearchQuery>;

export const PersonSearchResult = z.object({ actorId: ActorId, label: z.string(), countryCode: CountryCode.nullable(), world: PersonWorld });
export type PersonSearchResult = z.infer<typeof PersonSearchResult>;
/**
 * GET /api/v1/people/search?q=: online accounts whose display name is or starts with `q` AND whose setting lets the
 * searcher see their world (exact match first). Anyone hidden, offline or muted either way is simply absent.
 */
export const PersonSearchResponse = z.object({ items: z.array(PersonSearchResult) });
export type PersonSearchResponse = z.infer<typeof PersonSearchResponse>;

/**
 * `world.switch` (client -> its current room): move to another copy. `copy`: the Worlds list (arrive at the same spot of
 * the town: the street position, validated server-side as a free spot near it; inside a building, its door). `friend`:
 * "Join them in Finch" (the server checks the friend's setting and finds their copy; arrive on that copy's arrival
 * square, or at the building's door when asked from inside one: never at the friend's position). Answered with an
 * ordinary `room.transition_ready` ticket, or `world.switch_refused`.
 */
export const WorldSwitch = z.union([
  z.object({ copy: WorldCopyNumber }).strict(),
  z.object({ friendActorId: ActorId }).strict(),
]);
export type WorldSwitch = z.infer<typeof WorldSwitch>;

/**
 * Why a switch was refused (the visitor stays where they are):
 *  - ROOM_FULL: that copy has no place left; FRIEND_NOT_FOUND: the friend is not shown to you (offline, hidden, unknown:
 *    one answer for all); WORLD_CLOSED: that copy is not open any more; SAME_WORLD: you are already there; NOT_ON_FOOT:
 *    get out of the car first; RATE_LIMITED; NOT_AVAILABLE: not now (leaving, copies switched off, a server problem).
 */
export const WORLD_SWITCH_REFUSALS = ["ROOM_FULL", "FRIEND_NOT_FOUND", "WORLD_CLOSED", "SAME_WORLD", "NOT_ON_FOOT", "RATE_LIMITED", "NOT_AVAILABLE"] as const;
export const WorldSwitchRefusalCode = z.enum(WORLD_SWITCH_REFUSALS);
export type WorldSwitchRefusalCode = z.infer<typeof WorldSwitchRefusalCode>;
export const WorldSwitchRefused = z.object({ code: WorldSwitchRefusalCode, message: z.string().max(200) });
export type WorldSwitchRefused = z.infer<typeof WorldSwitchRefused>;

/** The answer shown when a friend is not shown to the searcher (offline, hidden or unknown alike). */
export const FRIEND_NOT_FOUND_MESSAGE = "We couldn't find them in any world right now.";
